import { maxBy, partition } from 'es-toolkit';

import { SolidData } from '@paragon/contract-library';
import { Direction3D, LineSegment3D, Point3D, Polygon3D, Polygons } from '@paragon/geometry-library';
import { circularTripletwise, groupAdjacentElementsCircularly, notNullish, sortBy } from '@paragon/utilities';

export const toPolygons = (solid: SolidData) => {
  const makePolygon = toPolygon(solid);
  return new Polygons(
    solid.faces.flatMap((face) => {
      const outer = makePolygon(face.outer);
      const inner = face.inner.map(makePolygon).filter(notNullish);
      return outer
        ? faceToPolygons(outer, inner)
        : inner.length
          ? quit('Tiny outer face has non-tiny inner faces')
          : [];
    }),
  );
};

const toPolygon = (solid: SolidData) => (loop: readonly number[]) => {
  let vertices = loop.map((index) => Point3D.fromObject(solid.vertices[index]));
  let oldVertexCount: number | null = null;
  while (vertices.length !== oldVertexCount) {
    oldVertexCount = vertices.length;
    vertices = circularTripletwise(
      groupAdjacentElementsCircularly(vertices, (vertex1, vertex2) => vertex1.equals(vertex2)).map((group) =>
        group[0].equals(group.at(-1)!) ? group[0] : quit('Could not coalesce zero-length segments'),
      ),
    )
      .map((triplet) => {
        const direction1 = Direction3D.fromPointToPoint(triplet[1], triplet[0]);
        const direction2 = Direction3D.fromPointToPoint(triplet[1], triplet[2]);
        if (direction1 == null && direction2 == null) return null;
        if (direction1 == null || direction2 == null) return triplet[1];
        return direction1.equals(direction2) ? null : triplet[1];
      })
      .filter(notNullish);
  }
  return vertices.length < 3 ? null : new Polygon3D(vertices);
};

export function faceToPolygons(exteriorRegion: Polygon3D, interiorRegions: readonly Polygon3D[]) {
  if (!interiorRegions.every((interiorRegion) => exteriorRegion.asPlane().equals(interiorRegion.asPlane())))
    quit('Regions are not coplanar');
  if (!interiorRegions.every(exteriorRegion.containsPolygon.bind(exteriorRegion)))
    quit('Interior regions are not contained by exterior region');
  const interiorNormalDirection = exteriorRegion.normalDirection.reverse();
  return faceToPolygonsRecursive(
    exteriorRegion,
    interiorRegions.map((region) => region.orientWith({ normalDirection: interiorNormalDirection })),
  );
}

/**
 * Assumes all passed polygons are coplanar, with exteriorRegion oriented oppositely to the interior regions.
 */
function faceToPolygonsRecursive(exteriorRegion: Polygon3D, interiorRegions: readonly Polygon3D[]): Polygon3D[] {
  if (!interiorRegions.length) return [exteriorRegion];

  // We "stitch" together the exterior loop and one interior loop to create two new exterior loops.
  // Then we divide the remaining interior loops based on their residency and recur.

  const die = () => quit('Could not convert face to polygon');

  // True iff the intersection of region and edge is at most a single point
  const validInteraction = (region: Polygon3D, edge: LineSegment3D) =>
    region.intersectionCoplanarPoints(edge).length <= 1 &&
    edge.endPoints.every((endpoint) => !region.containsOnInside(endpoint));

  // True iff the intersection of region's boundary and edge is at most a single point
  const validExteriorInteraction = (edge: LineSegment3D) => exteriorRegion.intersectionCoplanarPoints(edge).length <= 1;

  const findStitchingEdge = (exteriorVertices: readonly Point3D[], interiorVertices: readonly Point3D[]) =>
    sortBy(
      exteriorVertices.flatMap((exterior) => interiorVertices.map((interior) => new LineSegment3D(exterior, interior))),
      (edge) => edge.length,
    )
      .filter((edge) =>
        interiorRegions.every((region) => validInteraction(region, edge) && validExteriorInteraction(edge)),
      )
      .at(0) ?? die();

  let stitchingEdge1 = findStitchingEdge(
    exteriorRegion.vertices,
    interiorRegions.flatMap(({ vertices }) => vertices),
  );

  const connectedInteriorRegion =
    interiorRegions.find((region) => region.vertices.some((vertex) => stitchingEdge1.endPoint.equals(vertex))) ?? die();

  let stitchingEdge2 = findStitchingEdge(
    exteriorRegion.vertices.filter((vertex) => !vertex.equals(stitchingEdge1.basePoint)),
    connectedInteriorRegion.vertices.filter((vertex) => !vertex.equals(stitchingEdge1.endPoint)),
  );

  if (stitchingEdge1.insideIntersectionPoint(stitchingEdge2)) {
    // The edges cross. Swap points to undo the crossing.
    [stitchingEdge1, stitchingEdge2] = [
      new LineSegment3D(stitchingEdge1.basePoint, stitchingEdge2.endPoint),
      new LineSegment3D(stitchingEdge2.basePoint, stitchingEdge1.endPoint),
    ];
  }

  const edgesToWalk = exteriorRegion.edges
    .concat(connectedInteriorRegion.edges)
    .concat(stitchingEdge1, stitchingEdge1.reverse(), stitchingEdge2, stitchingEdge2.reverse());

  let current = stitchingEdge1;
  const firstEndpoint = current.basePoint;
  const firstEdges = [current];
  edgesToWalk.splice(edgesToWalk.indexOf(current), 1);
  while (!current.endPoint.equals(firstEndpoint)) {
    const loopCurrent = current;
    const next =
      maxBy(
        edgesToWalk.filter((edge) => edge.basePoint.equals(loopCurrent.endPoint)),
        (edge) => loopCurrent.direction.reverse().angleTo(edge.direction, exteriorRegion.normalDirection),
      ) ?? die();
    firstEdges.push(next);
    current = next;
    edgesToWalk.splice(edgesToWalk.indexOf(current), 1);
  }

  const first = new Polygon3D(firstEdges.map(({ basePoint }) => basePoint));
  const second = Polygon3D.fromUnorientedOrUnorderedSegments(edgesToWalk).orientWith(first);
  const [firstInteriorRegions, secondInteriorRegions] = partition(
    interiorRegions.filter((region) => !region.equals(connectedInteriorRegion)),
    (region) => first.containsPolygon(region) || (!second.containsPolygon(region) && die()),
  );

  return faceToPolygonsRecursive(first, firstInteriorRegions).concat(
    faceToPolygonsRecursive(second, secondInteriorRegions),
  );
}

function quit(message: string): never {
  throw new Error(message);
}
